import * as React from "react"
import NextLink from "next/link"
import type { LinkProps as NextLinkProps } from "next/link"
import { cn } from "@/lib/utils"

/**
 * Link — the canonical link atom. Wraps next/link for all navigation.
 *
 * External links (href starting with http/https/mailto/tel) automatically receive
 * rel="noopener noreferrer" unless explicitly overridden.
 *
 * @atomic-tier Atom
 * @category Navigation
 */

export interface LinkProps
  extends NextLinkProps,
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof NextLinkProps> {
  className?: string
}

function isExternal(href: LinkProps['href']): boolean {
  if (typeof href !== 'string') return false
  return /^(https?:\/\/|mailto:|tel:)/.test(href)
}

/**
 * Extract plain text from React children for auto-generating aria-label.
 * Only handles string children and shallow nesting — complex children
 * should pass an explicit aria-label prop instead.
 */
function extractText(children: React.ReactNode): string {
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  if (Array.isArray(children)) return children.map(extractText).join('')
  if (React.isValidElement(children)) {
    const props = children.props as Record<string, unknown>
    if (props.children) return extractText(props.children as React.ReactNode)
  }
  return ''
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, className, rel, children, "aria-label": ariaLabel, ...props }, ref) => {
    const external = isExternal(href)
    const resolvedRel = rel ?? (external ? 'noopener noreferrer' : undefined)
    const resolvedAriaLabel = ariaLabel || extractText(children) || undefined
    return (
      <NextLink
        ref={ref}
        href={href}
        className={cn(className)}
        rel={resolvedRel}
        aria-label={resolvedAriaLabel}
        {...props}
      >
        {children}
      </NextLink>
    )
  },
)
Link.displayName = "Link"

export { Link }
